import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Form, Button } from 'react-bootstrap';
import { Trash, Envelope, Telephone, GeoAlt, Globe } from 'react-bootstrap-icons';
import { AdminCard } from '../shared/AdminCard';
// Commented out - may need later
// import { PromoteProfessional } from './PromoteProfessional';

export function ProfessionalsDashboard({ idToken }) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [addStatus, setAddStatus] = useState(null);
  // Commented out - may need later
  // const [showPromoteModal, setShowPromoteModal] = useState(false);
  // const [promotedProfessional, setPromotedProfessional] = useState({});

  const fetchData = useCallback(async () => {
    if (!idToken) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_QMI_BASE_API}/professional`, {
        headers: { Authorization: idToken }
      });
      if (response.status === 200) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching professionals:", error);
    } finally {
      setIsLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddStatus(null);
    try {
      await axios.post(`${process.env.REACT_APP_CORE_API}/user/professional`, { email: newEmail }, {
        headers: { Authorization: idToken }
      });
      setAddStatus({ ok: true, msg: `Invite sent to ${newEmail}. They will get a temporary password by email.` });
      setNewEmail('');
    } catch (error) {
      setAddStatus({ ok: false, msg: error.response?.data?.detail || error.response?.data || 'Failed to add professional' });
    }
  };

  const handleDelete = async (email, index) => {
    try {
      await axios.delete(`${process.env.REACT_APP_QMI_BASE_API}/professional/${email}`, {
        headers: { Authorization: idToken }
      });
      const newData = [...data];
      newData.splice(index, 1);
      setData(newData);
    } catch (error) {
      console.error("Error deleting professional:", error);
    }
  };

  // Commented out - may need later
  // const handlePromoteClick = (prof) => {
  //   setPromotedProfessional(prof);
  //   setShowPromoteModal(true);
  // };

  // const onPromoteHide = () => {
  //   setShowPromoteModal(false);
  // };

  // Filter data based on search term
  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      item.email?.toLowerCase().includes(searchLower) ||
      item.industry?.toLowerCase().includes(searchLower) ||
      item.city?.toLowerCase().includes(searchLower) ||
      item.state?.toLowerCase().includes(searchLower) ||
      item.phone?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Professionals</h1>
        <p className="admin-page-description">
          Manage registered professionals
        </p>
      </div>

      <AdminCard>
        <Form onSubmit={handleAdd} className="d-flex gap-2 align-items-start flex-wrap">
          <Form.Control
            type="email"
            placeholder="professional@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            required
            style={{ maxWidth: 360 }}
          />
          <Button type="submit" disabled={!newEmail}>Add Professional</Button>
        </Form>
        {addStatus && (
          <div className={`mt-2 small ${addStatus.ok ? 'text-success' : 'text-danger'}`}>{addStatus.msg}</div>
        )}
      </AdminCard>

      <AdminCard noPadding className="mt-4">
        {/* Search */}
        <div style={{ padding: 'var(--space-4)' }}>
          <Form.Control
            type="text"
            placeholder="Search professionals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Desktop Table */}
        <div className="admin-table-wrapper admin-table-desktop">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Industry</th>
                <th>Location</th>
                <th>Phone</th>
                <th>Website</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="admin-empty-state">
                      <p className="admin-empty-description">No professionals found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredData.map((value, index) => (
                  <tr key={value.email || index}>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Envelope size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                        {value.email}
                      </span>
                    </td>
                    <td>{value.industry || '-'}</td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <GeoAlt size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                        {value.city && value.state ? `${value.city}, ${value.state}` : value.state || '-'}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Telephone size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                        {value.phone || '-'}
                      </span>
                    </td>
                    <td>
                      {value.website ? (
                        <a href={value.website} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--qmi-primary)' }}>
                          <Globe size={14} />
                          Visit
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="admin-table-actions">
                        {/* Commented out - may need later
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handlePromoteClick(value)}
                        >
                          Promote
                        </Button>
                        */}
                        <button
                          className="admin-btn-icon danger"
                          onClick={() => handleDelete(value.email, index)}
                          title="Delete professional"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="admin-mobile-cards" style={{ padding: 'var(--space-4)' }}>
          {filteredData.length === 0 ? (
            <div className="admin-empty-state">
              <p className="admin-empty-description">No professionals found</p>
            </div>
          ) : (
            filteredData.map((value, index) => (
              <div key={value.email || index} className="admin-mobile-card">
                <div className="admin-mobile-card-header">
                  <span className="admin-mobile-card-title">{value.email}</span>
                </div>
                <div className="admin-mobile-card-row">
                  <span className="admin-mobile-card-label">Industry</span>
                  <span className="admin-mobile-card-value">{value.industry || '-'}</span>
                </div>
                <div className="admin-mobile-card-row">
                  <span className="admin-mobile-card-label">Location</span>
                  <span className="admin-mobile-card-value">
                    {value.city && value.state ? `${value.city}, ${value.state}` : value.state || '-'}
                  </span>
                </div>
                <div className="admin-mobile-card-row">
                  <span className="admin-mobile-card-label">Phone</span>
                  <span className="admin-mobile-card-value">{value.phone || '-'}</span>
                </div>
                <div className="admin-mobile-card-actions">
                  {/* Commented out - may need later
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePromoteClick(value)}
                  >
                    Promote
                  </Button>
                  */}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(value.email, index)}
                  >
                    <Trash size={14} /> Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </AdminCard>

      {/* Commented out - may need later
      {showPromoteModal && (
        <PromoteProfessional
          idToken={idToken}
          show={showPromoteModal}
          onHide={onPromoteHide}
          professional={promotedProfessional}
        />
      )}
      */}
    </div>
  );
}
